import { useCallback } from "react";
import { useAppDispatch } from "store";
import { setSnackbar, removeSnackbar } from "store/commonSlice";
import { SnackbarType } from "components/Snackbar/types";
import { IconCheck, IconWarning, IconClose, IconTip } from "icons";

interface SnackbarOptions {
  id?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  persistent?: boolean;
}

export const snackbarIcons = {
  [SnackbarType.Success]: IconCheck,
  [SnackbarType.Error]: IconClose,
  [SnackbarType.Warning]: IconWarning,
  [SnackbarType.Info]: IconTip,
};

export const useSnackbar = () => {
  const dispatch = useAppDispatch();

  const showSnackbar = useCallback(
    (message: string, type: SnackbarType = SnackbarType.Info, options?: SnackbarOptions) => {
      const id = options?.id || `snackbar-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      dispatch(
        setSnackbar({
          id,
          message,
          type,
          icon: snackbarIcons[type],
          duration: options?.duration,
          action: options?.action,
          persistent: options?.persistent,
        })
      );

      return id;
    },
    [dispatch]
  );

  const showSuccess = useCallback(
    (message: string, options?: SnackbarOptions) => {
      return showSnackbar(message, SnackbarType.Success, options);
    },
    [showSnackbar]
  );

  const showError = useCallback(
    (message: string, options?: SnackbarOptions) => {
      showSnackbar(message, SnackbarType.Error, { ...options, persistent: options?.persistent ?? true });
    },
    [showSnackbar]
  );

  const showWarning = useCallback(
    (message: string, options?: SnackbarOptions) => {
      showSnackbar(message, SnackbarType.Warning, { ...options, persistent: options?.persistent ?? true });
    },
    [showSnackbar]
  );

  const showInfo = useCallback(
    (message: string, options?: SnackbarOptions) => {
      showSnackbar(message, SnackbarType.Info, { ...options, persistent: options?.persistent ?? true });
    },
    [showSnackbar]
  );

  const showUploadError = useCallback(
    (message: string, options?: SnackbarOptions) => {
      return showSnackbar(message, SnackbarType.Warning, { ...options, persistent: false });
    },
    [showSnackbar]
  );

  const dismiss = useCallback(
    (id: string) => {
      dispatch(removeSnackbar(id));
    },
    [dispatch]
  );

  return {
    showSnackbar,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showUploadError,
    dismiss,
  };
};
